import { Button, EmptyState, Field, Spinner } from '@freightapi/ui'
import { createFileRoute } from '@tanstack/react-router'
import { type FormEvent, useCallback, useEffect, useState } from 'react'
import { TwoFactorSetup } from '../components/TwoFactorSetup'
import {
  ApiError,
  asApiError,
  fetchTwoFactor,
  redeemRecoveryCode,
  signOut,
  type TwoFactorStatus,
  verifyTwoFactorCode,
} from '../lib/api'
import { useConsoleI18n } from '../lib/locale'

/**
 * The code prompt at sign-in (M4-12), and the enrolment screen for the member whose organisation
 * has just started requiring a second factor.
 *
 * It sits outside the `/_app` layout, next to `/login` and `/welcome`, for the reason that layout
 * states itself: a navigation bar to pages you cannot open yet is noise. A session that owes a
 * factor is authenticated and refused by `middleware/two-factor.ts` on every console call except
 * the handful that lead out of here, so the sidebar would be a list of 403s.
 *
 * Which of the two screens this is comes from the API and not from a guess in the browser. The
 * middleware and this screen answer the same question — "what does this session still owe?" — and
 * the API is the one that gets to answer it.
 */
export const Route = createFileRoute('/two-factor')({ component: TwoFactorGate })

type State =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly two_factor: TwoFactorStatus }
  | { readonly status: 'error'; readonly message: string }

function TwoFactorGate() {
  const { t } = useConsoleI18n()
  const [state, setState] = useState<State>({ status: 'loading' })

  const load = useCallback(async () => {
    try {
      setState({ status: 'ready', two_factor: await fetchTwoFactor() })
    } catch (cause) {
      if (cause instanceof ApiError && cause.isUnauthenticated) {
        window.location.assign('/login')
        return
      }
      const failure = asApiError(cause)
      setState({ status: 'error', message: t(failure.messageKey) })
    }
  }, [t])

  useEffect(() => {
    void load()
  }, [load])

  // A full page load rather than a router navigation: everything inside the console renders from
  // the session, and that answer has just changed on the server.
  const enter = useCallback(() => window.location.assign('/'), [])

  if (state.status === 'loading') {
    return (
      <div className="flex justify-center py-10">
        <Spinner label={t('loading')} size="lg" />
      </div>
    )
  }
  if (state.status === 'error') {
    return <EmptyState title={t('error.title')} description={state.message} />
  }

  const { enrolled, required, verified } = state.two_factor

  // Nothing owed: somebody bookmarked this page, or verified it in another tab.
  if (verified || !(enrolled || required)) {
    return (
      <section className="mx-auto flex max-w-md flex-col gap-4">
        <h1 className="font-semibold text-display text-ink">{t('two_factor.done.title')}</h1>
        <p className="text-body text-muted">{t('two_factor.done.body')}</p>
        <Button onClick={enter}>{t('two_factor.done.continue')}</Button>
      </section>
    )
  }

  if (!enrolled) {
    return (
      <section className="mx-auto flex max-w-2xl flex-col gap-6">
        <div className="flex flex-col gap-2">
          <h1 className="font-semibold text-display text-ink">{t('two_factor.required.title')}</h1>
          <p className="text-body text-muted">{t('two_factor.required.body')}</p>
        </div>
        <TwoFactorSetup onCompleted={enter} />
      </section>
    )
  }

  return <CodePrompt onVerified={enter} />
}

/**
 * Six digits, or one recovery code. Both are the same question to the person in front of the
 * screen and two different endpoints behind it, so the form switches rather than guessing from the
 * shape of what was typed — a guess that would send a mistyped recovery code to the TOTP endpoint
 * and spend one of the ten attempts in the rate-limit window on a certainty.
 */
function CodePrompt({ onVerified }: { readonly onVerified: () => void }) {
  const { t } = useConsoleI18n()
  const [mode, setMode] = useState<'totp' | 'recovery'>('totp')
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | undefined>(undefined)
  const [busy, setBusy] = useState(false)

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setBusy(true)
    setError(undefined)
    try {
      if (mode === 'totp') await verifyTwoFactorCode(code)
      else await redeemRecoveryCode(code)
      onVerified()
    } catch (cause) {
      const failure = asApiError(cause)
      // The API answers the same problem for wrong, expired and already used, and so does this.
      // A 429 is the one failure worth naming: ten attempts per ten minutes (M4-12), and a person
      // who has hit that wants to know to wait rather than to keep typing.
      setError(failure.status === 429 ? t('two_factor.error.too_many') : t(failure.messageKey))
      setCode('')
      setBusy(false)
    }
  }

  return (
    <section className="mx-auto flex max-w-md flex-col gap-5">
      <h1 className="font-semibold text-display text-ink">{t('two_factor.prompt.title')}</h1>
      <p className="text-body text-muted">
        {mode === 'totp' ? t('two_factor.prompt.body') : t('two_factor.prompt.recovery_body')}
      </p>

      <form className="flex flex-col gap-4" onSubmit={(event) => void onSubmit(event)}>
        <Field
          label={mode === 'totp' ? t('two_factor.code.label') : t('two_factor.recovery.label')}
          hint={mode === 'totp' ? t('two_factor.code.hint') : t('two_factor.recovery.hint')}
          error={error}
          required
        >
          {({ id, describedBy, invalid }) => (
            <input
              id={id}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              type="text"
              // The browser offers the code from the authenticator, and nothing autocorrects it.
              inputMode={mode === 'totp' ? 'numeric' : 'text'}
              autoComplete="one-time-code"
              autoCorrect="off"
              autoCapitalize="characters"
              spellCheck={false}
              // This screen is one field and one button, and the person reached it holding a
              // phone that shows a code expiring in seconds.
              // biome-ignore lint/a11y/noAutofocus: the only interactive element on the screen
              autoFocus
              maxLength={mode === 'totp' ? 9 : 24}
              required
              value={code}
              onChange={(event) => setCode(event.target.value)}
              className="min-h-touch rounded-card border border-line bg-surface px-3 text-center font-mono text-ink text-lead tracking-[0.2em]"
            />
          )}
        </Field>
        <Button type="submit" block busy={busy} busyLabel={t('loading')}>
          {t('two_factor.prompt.submit')}
        </Button>
      </form>

      <div className="flex flex-col items-start gap-2">
        <button
          type="button"
          onClick={() => {
            setMode(mode === 'totp' ? 'recovery' : 'totp')
            setCode('')
            setError(undefined)
          }}
          className="min-h-touch text-body text-brand-700 underline"
        >
          {mode === 'totp' ? t('two_factor.prompt.use_recovery') : t('two_factor.prompt.use_totp')}
        </button>
        <button
          type="button"
          onClick={() => {
            void signOut()
              .catch(() => undefined)
              .then(() => window.location.assign('/login'))
          }}
          className="min-h-touch text-body text-muted underline"
        >
          {t('button.sign_out')}
        </button>
      </div>
      <p className="text-caption text-muted">{t('two_factor.prompt.lost')}</p>
    </section>
  )
}
